import { toPng } from 'html-to-image';

/** Deckelt die Aufnahme auf ~4 MP, damit toPng auf großen Seiten nicht unnötig lange blockiert. */
const MAX_PIXELS = 4_000_000;
/** Browser-Canvas bricht jenseits von ~16.384 px still ab (leeres/verzerrtes Bild). */
const MAX_EDGE = 12_000;
/** BFF nimmt Payloads bis 10 MB JSON entgegen; Base64 blaeht Bilddaten um Faktor 4/3 auf. */
const MAX_DATA_URL_CHARS = 6_000_000;

/**
 * Blendet das Melde-Werkzeug selbst aus dem Screenshot aus — ohne diesen
 * Filter fotografiert sich der Button/Dialog/die Fortschrittsanzeige mit.
 * Die MUI-Backdrop-Ausnahme verhindert einen grauen Schleier über dem
 * gesamten Bild, wenn der Dialog beim Klick noch nicht geschlossen ist.
 */
export function screenshotFilter(node: Node): boolean {
  if (node instanceof HTMLElement) {
    if (node.hasAttribute('data-bug-report-button')) return false;
    if (node.hasAttribute('data-bug-report-dialog')) return false;
    if (node.hasAttribute('data-bug-report-progress')) return false;
    if (node.classList?.contains('MuiBackdrop-root')) return false;
  }
  return true;
}

/**
 * Entriegelt alle vertikal scrollenden Container UND deren Vorfahren bis
 * `html`, damit auch nicht sichtbare Scrollbereiche mit aufs Bild kommen.
 * Gibt eine `restore()`-Funktion zurück, die die ursprünglichen `cssText`
 * bitgenau zurückschreibt — MUSS im `finally` der Aufnahme aufgerufen
 * werden, sonst bleibt die Anwendung optisch zerlegt zurück, falls die
 * Aufnahme selbst abbricht.
 */
export function expandScrollContainers(root: Document = document): () => void {
  const saved: Array<[HTMLElement, string]> = [];
  const seen = new Set<HTMLElement>();

  const relax = (el: HTMLElement) => {
    if (seen.has(el)) return;
    seen.add(el);
    saved.push([el, el.style.cssText]);
    el.style.setProperty('height', 'auto', 'important');
    el.style.setProperty('max-height', 'none', 'important');
    el.style.setProperty('overflow', 'visible', 'important');
  };

  for (const el of Array.from(root.querySelectorAll<HTMLElement>('*'))) {
    if (el.scrollHeight <= el.clientHeight + 1) continue;
    const oy = getComputedStyle(el).overflowY;
    if (oy !== 'auto' && oy !== 'scroll' && oy !== 'overlay') continue;
    // Vorfahren mit fixer/100vh-Höhe würden das Kind sonst weiter abschneiden.
    let cur: HTMLElement | null = el;
    while (cur) {
      relax(cur);
      cur = cur.parentElement;
    }
  }
  if (root.documentElement) relax(root.documentElement);
  if (root.body) relax(root.body);

  return () => {
    for (const [el, cssText] of saved) el.style.cssText = cssText;
  };
}

/** Skalierungsfaktor (≤1), der die Aufnahme unter MAX_PIXELS und MAX_EDGE hält. Nie beschnitten, nur nachskaliert. */
export function computePixelRatio(w: number, h: number): number {
  if (w <= 0 || h <= 0) return 1;
  return Math.min(1, Math.sqrt(MAX_PIXELS / (w * h)), MAX_EDGE / w, MAX_EDGE / h);
}

/** Skaliert eine data-URL über ein Zwischen-Canvas nach, falls sie das Body-Limit sprengen würde. */
export async function rescaleDataUrl(dataUrl: string, factor: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * factor));
      canvas.height = Math.max(1, Math.round(img.height * factor));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas-Kontext für die Nachskalierung nicht verfügbar'));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Screenshot konnte nicht nachskaliert werden'));
    img.src = dataUrl;
  });
}

/**
 * Nimmt einen Ganzseiten-Screenshot auf — inklusive aller nicht sichtbaren
 * Scrollbereiche. Wirft bei Fehlschlag (der Aufrufer fängt das ab und
 * öffnet den Dialog trotzdem, nur ohne Bild — siehe BugReportButton.tsx).
 *
 * Liefert neben der data-URL die aufgenommenen Maße + den Skalierungsfaktor —
 * der BugReportButton protokolliert sie zur Diagnose (Seitengröße/Knotenzahl).
 */
export interface FullPageCapture {
  dataUrl: string;
  width: number;
  height: number;
  pixelRatio: number;
}

export async function captureFullPage(): Promise<FullPageCapture> {
  const restore = expandScrollContainers(document);
  try {
    const width = Math.max(document.documentElement.scrollWidth, document.body.scrollWidth);
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    const pixelRatio = computePixelRatio(width, height);

    let dataUrl = await toPng(document.body, {
      filter: screenshotFilter,
      cacheBust: true,
      width,
      height,
      pixelRatio,
      style: { width: `${width}px`, height: `${height}px`, overflow: 'visible' },
    });

    if (dataUrl.length > MAX_DATA_URL_CHARS) {
      const factor = Math.sqrt(MAX_DATA_URL_CHARS / dataUrl.length);
      dataUrl = await rescaleDataUrl(dataUrl, factor);
    }

    return { dataUrl, width, height, pixelRatio };
  } finally {
    restore();
  }
}
